import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaUserShield, FaSearch, FaTimes, FaShieldAlt } from 'react-icons/fa';
import Swal from 'sweetalert2';
import DataTable from '../../components_shared/DataTable';
import { roleAPI } from '../../utils/api';
import RoleCreate from './RoleCreate';

export default function RolesTable() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, role: null });
  const [search, setSearch] = useState('');

  useEffect(() => { fetchRoles(); }, []);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const res = await roleAPI.getRoles();
      const roles = Array.isArray(res.data) ? res.data : (res.data.content || []);
      setData(roles);
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = search
    ? data.filter(r =>
        r.name?.toLowerCase().includes(search.toLowerCase()) ||
        r.description?.toLowerCase().includes(search.toLowerCase())
      )
    : data;

  const handleDelete = async role => {
    const result = await Swal.fire({
      title: 'Delete Role?',
      html: `Remove <strong>${role.name}</strong>?<br/><span style="font-size:0.85rem;color:#94a3b8">Users assigned this role will lose related access.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    setLoading(true);
    try {
      await roleAPI.deleteRole(role.id);
      Swal.fire({ title: 'Deleted!', text: `Role "${role.name}" removed.`, icon: 'success', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
      await fetchRoles();
    } catch (err) {
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Role',
      cell: ({ row }) => {
        const name = row.original.name || '';
        const clean = name.replace('ROLE_', '');
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: 'rgba(15,111,173,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#0f6fad', fontSize: '0.9rem',
            }}>
              <FaUserShield />
            </div>
            <div>
              <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>{clean}</div>
              <div style={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{name}</div>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ getValue }) => getValue()
        ? <span style={{ color: '#374151', fontSize: '0.875rem' }}>{getValue()}</span>
        : <span style={{ color: '#cbd5e1', fontStyle: 'italic', fontSize: '0.85rem' }}>No description</span>,
    },
    {
      header: 'Permissions',
      cell: ({ row }) => {
        const count = row.original.permissions?.length || 0;
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: count > 0 ? 'rgba(16,172,132,0.1)' : '#f1f5f9',
            color: count > 0 ? '#10ac84' : '#94a3b8',
            padding: '4px 10px', borderRadius: 20,
            fontSize: '0.75rem', fontWeight: 700,
          }}>
            <FaShieldAlt style={{ fontSize: '0.6rem' }} /> {count} {count === 1 ? 'permission' : 'permissions'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <ActionBtn icon={<FaEdit />} title="Edit" color="#0f6fad" onClick={() => setModal({ open: true, role: row.original })} />
          <ActionBtn icon={<FaTrash />} title="Delete" color="#e74c3c" onClick={() => handleDelete(row.original)} />
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Stats */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <StatCard value={data.length} label="Total Roles" color="#0f6fad" />
        <StatCard value={data.filter(r => (r.permissions?.length || 0) > 0).length} label="Configured Roles" color="#10ac84" />
        <StatCard value={data.filter(r => (r.permissions?.length || 0) === 0).length} label="Unconfigured Roles" color="#f39c12" />
      </div>

      {/* Main card */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '22px 28px 0', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
              <FaUserShield style={{ color: '#0f6fad' }} /> Role Management
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: '#94a3b8' }}>Define system access groups and their primary functions</p>
          </div>
          <button onClick={() => setModal({ open: true, role: null })} disabled={loading} style={primaryBtn}>
            <FaPlus /> Define New Role
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: '16px 28px', borderBottom: '1px solid #f0f4f8' }}>
          <div style={searchBoxStyle}>
            <FaSearch style={{ color: '#94a3b8', fontSize: '0.8rem', marginRight: 8, flexShrink: 0 }} />
            <input
              type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search roles..."
              style={searchInputStyle}
            />
            {search && (
              <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, display: 'flex', alignItems: 'center' }}>
                <FaTimes style={{ fontSize: '0.75rem' }} />
              </button>
            )}
          </div>
        </div>

        <div style={{ padding: '0 28px 24px' }}>
          <DataTable data={filtered} columns={columns} loading={loading} />
        </div>
      </div>

      {/* Create / Edit modal */}
      {modal.open && (
        <ModalOverlay onClose={() => setModal({ open: false, role: null })}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 0' }}>
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
              {modal.role ? 'Edit Role' : 'Create New Role'}
            </h3>
            <button onClick={() => setModal({ open: false, role: null })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '1.1rem', padding: 4 }}>
              <FaTimes />
            </button>
          </div>
          <div style={{ padding: '12px 24px 24px' }}>
            <RoleCreate role={modal.role} onClose={() => setModal({ open: false, role: null })} onSaved={fetchRoles} />
          </div>
        </ModalOverlay>
      )}

    </div>
  );
}

/* ---- Shared helpers ---- */
function StatCard({ value, label, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #f0f4f8', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color, fontSize: '1.3rem', fontWeight: 700, flexShrink: 0 }}>{value}</div>
      <div style={{ fontSize: '0.78rem', color: '#94a3b8', fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function ActionBtn({ icon, title, color, onClick }) {
  const [h, setH] = useState(false);
  return (
    <button title={title} onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ width: 30, height: 30, borderRadius: 7, background: h ? `${color}18` : 'transparent', border: `1px solid ${h ? color : 'transparent'}`, color, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', transition: 'all 0.15s', padding: 0 }}>
      {icon}
    </button>
  );
}

function ModalOverlay({ children, onClose, maxWidth = 480 }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        {children}
      </div>
    </div>
  );
}

const cardStyle = { background: '#fff', borderRadius: 14, border: '1px solid #f0f4f8', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden' };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 8, background: 'linear-gradient(135deg, #10ac84, #0e9a75)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins',sans-serif", boxShadow: '0 2px 8px rgba(16,172,132,0.25)' };
const searchBoxStyle = { display: 'flex', alignItems: 'center', background: '#f8fafc', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '0 12px', height: 40, maxWidth: 380 };
const searchInputStyle = { flex: 1, border: 'none', background: 'transparent', fontSize: '0.875rem', color: '#374151', outline: 'none', fontFamily: "'Poppins',sans-serif" };
